import { Box, Divider, Stack, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { apiQueryKey, useDeleteSlackChannel } from '@repo/api-codegen';
import { SettingsBlock } from '@repo/common/components/index.ts';
import { integrationList } from '@repo/common/constants/integration/integrations.ts';
import { EntityType } from '@repo/common/enums/entityType';
import type { SlackIntegrationSpec } from '@repo/common/interfaces/integrationSpec.ts';
import { Button } from '@repo/foundations';
import { useMutation } from '@tanstack/react-query';
import { isNil, size } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import type { ISecodaSlackChannel } from '../../../api';
import { channelTest, queryClient, useIntegration } from '../../../api';
import type { FetchModelListHook } from '../../../api/factories/types.ts';
import { useForceReRender } from '../../../utils/hook/useForceReRender.ts';
import { closeSpotlight } from '../../Spotlight';
import { TableV2 } from '../../TableV2';
import { SlackOAuthButton } from '../SlackOAuthButton.tsx';
import { useSecodaChannelList } from './SecodaChannelHooks.hooks.tsx';
import { useColumns } from './SecodaChannelHooks.tsx';
import { SlackChannelModal } from './SlackChannelModal.tsx';

interface IIntegrationSlackChannelsProps {
	integrationId: string;
}

const QUICK_ACTIONS = [
	'actions::test_channel',
	'actions::edit',
	'actions::delete',
] as const;

/**
 * IntegrationSlackChannels Component.
 * It fetches integration data, manages its Slack channels, and updates the changes.
 *
 * @param {IIntegrationSlackChannelsProps} props - Component properties.
 * @param {string} props.integrationId - ID of the integration.
 *
 * @returns {JSX.Element} The rendered component.
 */
function IntegrationSlackChannels({
	integrationId,
}: IIntegrationSlackChannelsProps): JSX.Element {
	// Fetch the integration data by its id
	const { data: integration } = useIntegration({
		id: integrationId,
		options: {
			enabled: !!integrationId,
		},
	});

	const secodaChannelList = useSecodaChannelList();
	const { data: secodaChannels } = secodaChannelList({});

	const columns = useColumns();

	const theme = useMantineTheme();

	// Initialize the state variables for webhook and data request channels
	const [webHookChannel, setWebHookChannel] = useState<string>('');
	const [incidentsChannel, setIncidentsChannel] = useState<string>('');
	const [dataRequestChannel, setDataRequestChannel] = useState<string>('');
	const [forceRefresh, setForceRefresh] = useState<boolean>(false);
	const [editChannel, setEditChannel] = useState<
		ISecodaSlackChannel | undefined
	>(undefined);

	const [
		channelModalOpened,
		{ close: closeChannelModal, open: openChannelModal },
	] = useDisclosure(false);

	const { forceUpdate, key } = useForceReRender();

	const handleCloseModal = () => {
		setEditChannel(undefined);
		closeChannelModal();
		forceUpdate();
	};

	const handleOpenModal = (channel: ISecodaSlackChannel | undefined) => {
		setEditChannel(channel);
		openChannelModal();
		forceUpdate();
	};

	const { mutateAsync: deleteChannel } = useDeleteSlackChannel({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: apiQueryKey('integration_v2/slack/channel'),
			});
		},
	});

	const { reset: resetTestMessage, mutate: testMessageMutate } = useMutation({
		async mutationFn(channel: string) {
			const response = await channelTest(channel);
			return response.data;
		},
		onSuccess: (channel: string) => {
			resetTestMessage();
			showNotification({
				title: 'Test message successful',
				message: 'A message has been sent to the channel ' + channel,
				color: 'green',
			});
		},
		onError: (channel: string) => {
			resetTestMessage();
			showNotification({
				title: 'Access request failed to send',
				message: 'Test message failed for channel ' + channel,
				color: 'red',
			});
		},
	});
	const [testChannel, setTestChannel] = useState<string>('');
	const sendTestMessage = () => {
		if (testChannel !== '') {
			testMessageMutate(testChannel);
		}
	};

	// Define a callback to update the state of the webhook and data request channels
	const updateHookChannelCallback = useCallback(() => {
		if (isNil(integration)) {
			return;
		}

		setWebHookChannel(
			(integration.credentials?.webhook_channel_id ?? '') as string
		);
		setIncidentsChannel(
			(integration.credentials?.incidents_channel_id ?? '') as string
		);
		setDataRequestChannel(
			(integration.credentials?.data_request_channel_id ?? '') as string
		);
	}, [integration]);

	// Use the callback to update the state whenever the integration data changes
	// eslint-disable-next-line no-use-effect/no-use-effect
	useEffect(() => {
		updateHookChannelCallback();
	}, [updateHookChannelCallback]);

	return (
		<Stack>
			<Divider my={theme.spacing['3xs']} />
			<Box>
				<SettingsBlock
					title="Re-authorization"
					description="Retrieve new access tokens for your Slack extension"
					rightComponent={
						<SlackOAuthButton
							spec={
								integrationList.find(
									(i) => i.type === 'slack'
								) as SlackIntegrationSpec
							}
							integration={integration}
						/>
					}
				/>
			</Box>
			<Divider my={theme.spacing['3xs']} />
			<TableV2
				usePaginationList={
					secodaChannelList as FetchModelListHook<ISecodaSlackChannel>
				}
				columns={columns}
				height={secodaChannels?.count === 0 ? 228 : undefined}
				withCheckbox
				pluralTypeString="channels"
				withSearch
				withAdditionalButtons={
					<Button onClick={() => handleOpenModal(undefined)}>
						Add channel
					</Button>
				}
				// FIXME: move to useActions similar to columns
				withActions={[
					{
						id: 'actions::test_channel',
						title: 'Test channel',
						name: 'Test channel',
						iconName: 'send' as const,
						hotkey: '/tc',
						type: EntityType.all,
						team: undefined,
						category: 'actions',
						show: true,
						onClick: (selected) => {
							selected.map((channel) => {
								resetTestMessage();
								setTestChannel(channel.channel_id);
								sendTestMessage();
							});
						},
					},
					{
						id: 'actions::edit',
						title: 'Edit channel',
						name: 'Edit channel',
						iconName: 'edit' as const,
						hotkey: '/ec',
						type: EntityType.all,
						team: undefined,
						category: 'actions',
						show: (selected: ISecodaSlackChannel[]) => size(selected) === 1,
						onClick: (selected, clearSelected) => {
							handleOpenModal(selected[0]);
							clearSelected();
							closeSpotlight('bulkActions');
						},
					},
					{
						id: 'actions::delete',
						title: 'Delete',
						name: 'Delete',
						iconName: 'trash' as const,
						hotkey: '/sp',
						type: EntityType.all,
						team: undefined,
						category: 'actions',
						show: true,
						onClick: async (selected: ISecodaSlackChannel[], clearSelected) => {
							selected.map((channel) => {
								deleteChannel({
									pathParams: {
										channelId: channel.id,
									},
								});
								clearSelected();
								closeSpotlight('bulkActions');
							});
						},
					},
				]}
				withQuickActions={QUICK_ACTIONS}
			/>

			<SlackChannelModal
				key={key}
				opened={channelModalOpened}
				onClose={handleCloseModal}
				existingChannel={editChannel}
			/>
		</Stack>
	);
}

export default IntegrationSlackChannels;
